import { FocusOrigin } from '@angular/cdk/a11y';

export enum DialogCloseReason {
  Save = 'save',
  Update = 'update',
  Cancel = 'cancel',
  Confirm = 'confirm',
  Escape = 'escape',
  // DIALOG closed via BACKDROP click
  Backdrop = 'backdrop',
  // User chose to SAVE changes and close (from unsaved changes prompt)
  SaveAndClose = 'save-and-close',
  // User chose to DISCARD changes and close (from unsaved changes prompt)
  DiscardChanges = 'discard-changes',
}

/**
 * Standardized dialog close event.
 *
 * This is what consumers receive when subscribing to `dialogRef.closed`.
 * It provides a flat, predictable structure whether the dialog was closed:
 * * explicitly (Save, Cancel, etc.)
 * * implicitly (ESC, backdrop, programmatic)
 *
 * @template TData The type of data returned from the dialog.
 *
 * @example
 * dialogRef.closed.subscribe((event: DialogCloseEvent<MyData>) => {
 *   if (event.reason === DialogCloseReason.Save) {
 *     console.log('Saved:', event.data);
 *   }
 *   if (event.reason === DialogCloseReason.Escape) {
 *     console.log('Closed via ESC');
 *   }
 * });
 */
export interface DialogCloseEvent<TData = unknown> {
  reason: DialogCloseReason;
  data?: TData;
  focusOrigin?: FocusOrigin;
}

/**
 * Options for closing a dialog programmatically.
 *
 * Passed to `dialogRef.close(options)` or `dirtyHandler.tryClose(options)`.
 * Represents the caller’s intent (not the final interpreted event).
 *
 * @template TData The type of data returned from the dialog.
 *
 * @example Save:
 * dialogRef.close({
 *   reason: DialogCloseReason.Save,
 *   data: formData
 * });
 *
 * @example Cancel:
 * dialogRef.close({
 *   reason: DialogCloseReason.Cancel
 * });
 */
export interface DialogCloseOptions<TData = unknown> {
  reason: DialogCloseReason;
  data?: TData;
  focusOrigin?: FocusOrigin;
}
